import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";

import { type PendingDocumentResponse } from "@/api-v3";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { ErrorRetryComponent } from "@/components/error-card/error-retry";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { getDefaultContractsFields } from "@/features/auth/components/auth-form/helpers";
import { APP_NAME, useTenantDisplayName } from "@/features/branding";

import { getContractLabelText } from "../../helpers/get-contract-label-text";

const getLegalDocumentsAgreementFormSchema = () =>
  z.record(
    z.string(),
    z.boolean({ message: "" }).refine((value) => value === true, {
      message: "",
    })
  );

interface ContractsComponentProps {
  open: boolean;
  contracts: PendingDocumentResponse[];
  isSubmitting: boolean;
  isError: boolean;
  refetch: () => void;
  onAccept: () => void;
}

export const LegalDocumentsAgreementDialog = ({
  open,
  contracts,
  isSubmitting = false,
  isError,
  refetch,
  onAccept,
}: ContractsComponentProps) => {
  const { t } = useTranslation();
  const clientName = useTenantDisplayName() ?? APP_NAME;
  const contractIds = useMemo(
    () => contracts.map((contract) => contract.id),
    [contracts]
  );

  const form = useForm({
    mode: "onChange",
    resolver: zodResolver(getLegalDocumentsAgreementFormSchema()),
    defaultValues: getDefaultContractsFields(contractIds),
  });

  useEffect(() => {
    form.reset(getDefaultContractsFields(contractIds));
  }, [contractIds, form]);

  return (
    <ConfirmationDialog
      onOpenChange={() => {
        return;
      }}
      label="legal-documents-agreement-dialog"
      className="max-w-md"
      dismissible={false}
      open={open}
      title={t("legal_documents_agreement")}
      message={
        <div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onAccept)} className="space-y-8">
              <FormItem>
                <div className="mb-4">
                  <FormDescription>
                    {t("legal_documents_agreement_description")}
                  </FormDescription>
                </div>
                {isError ? (
                  <ErrorRetryComponent retry={refetch} />
                ) : (
                  <>
                    {contracts.map((contract) => {
                      const contractDisplayText = getContractLabelText(
                        contract.title,
                        clientName,
                        t
                      );

                      return (
                        <FormField
                          key={contract.id}
                          control={form.control}
                          name={contract.id}
                          render={({ field, fieldState }) => {
                            return (
                              <FormItem>
                                <div className="flex items-center flex-row gap-2">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value ?? false}
                                      onCheckedChange={field.onChange}
                                      error={fieldState.invalid}
                                    />
                                  </FormControl>
                                  <FormLabel className="text-sm cursor-pointer leading-relaxed">
                                    <span>
                                      {t("accept")}{" "}
                                      {contract.version.document_url ? (
                                        <a
                                          href={contract.version.document_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary hover:underline font-medium"
                                        >
                                          {contractDisplayText}
                                        </a>
                                      ) : (
                                        contractDisplayText
                                      )}
                                      .
                                    </span>
                                  </FormLabel>
                                </div>
                              </FormItem>
                            );
                          }}
                        />
                      );
                    })}
                  </>
                )}
              </FormItem>
              <div className="flex justify-end">
                <Button
                  disabled={!form.formState.isValid}
                  loading={isSubmitting}
                  type="submit"
                >
                  {t("accept")}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      }
      hideCancelButton={true}
      hideConfirmation={true}
    />
  );
};
